import { useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import PageHeader from "../components/PageHeader";
import CallToAction from "../components/CallToAction";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";

// Blog categories
const categories = [
  { id: "all", name: "Vsi članki" },
  { id: "face", name: "Obraz" },
  { id: "body", name: "Telo" },
  { id: "skincare", name: "Nega kože" },
  { id: "procedures", name: "Posegi" },
  { id: "aftercare", name: "Okrevanje" }
];

// Mock blog posts data
const blogPosts = [
  {
    id: 1,
    title: "Vse, kar morate vedeti o korekciji vek",
    slug: "vse-o-korekciji-vek",
    excerpt: "Korekcija vek je eden najbolj priljubljenih estetskih posegov, ki lahko dramatično pomladi vaš videz. Preberite več o postopku, okrevanju in rezultatih.",
    imageUrl: "https://images.unsplash.com/photo-1571942794135-56e61c8ac80e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    category: "face",
    date: "2024-04-15",
    author: "Dr. Danijel Novak",
    readTime: "5 min"
  },
  {
    id: 2,
    title: "Kako pripraviti kožo na poletje",
    slug: "priprava-koze-na-poletje",
    excerpt: "Z bližajočim se poletjem je pomembno, da svojo kožo primerno pripravite na sonce in visoke temperature. Ti nasveti vam bodo pomagali ohraniti zdrav in sijoč videz.",
    imageUrl: "https://images.unsplash.com/photo-1555820585-c5ae44394b79?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    category: "skincare",
    date: "2024-04-10",
    author: "Maja Kovač",
    readTime: "4 min"
  },
  {
    id: 3,
    title: "Miti in resnice o povečanju prsi",
    slug: "miti-resnice-povecanje-prsi",
    excerpt: "Povečanje prsi je obdano s številnimi miti. Ta članek razkriva resnice in podira najpogostejše zmote o tem priljubljenem posegu.",
    imageUrl: "https://images.unsplash.com/photo-1579684453377-48ec05c6b30b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    category: "body",
    date: "2024-04-05",
    author: "Dr. Danijel Novak",
    readTime: "7 min"
  },
  {
    id: 4,
    title: "Vodnik za okrevanje po abdominoplastiki",
    slug: "vodnik-okrevanje-abdominoplastika",
    excerpt: "Pravilno okrevanje po abdominoplastiki je ključnega pomena za optimalne rezultate. Sledite našemu podrobnemu vodniku za hitrejše in lažje okrevanje.",
    imageUrl: "https://images.unsplash.com/photo-1532466255339-9d5a715a69e8?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    category: "aftercare",
    date: "2024-03-28",
    author: "Ana Horvat",
    readTime: "6 min"
  },
  {
    id: 5,
    title: "Najnovejše tehnike pomladitve obraza",
    slug: "najnovejse-tehnike-pomladitve",
    excerpt: "Spoznajte najnovejše tehnike in postopke za pomladitev obraza, ki jih uporabljamo v naši kliniki. Minimalni posegi, maksimalni rezultati.",
    imageUrl: "https://images.unsplash.com/photo-1600334129128-685c5582fd35?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    category: "face",
    date: "2024-03-20",
    author: "Dr. Danijel Novak",
    readTime: "5 min"
  },
  {
    id: 6,
    title: "Kako izbrati pravi poseg za vaše potrebe",
    slug: "izbira-pravega-posega",
    excerpt: "Odločitev za estetski poseg je pomembna. Ta vodnik vam bo pomagal razumeti različne možnosti in izbrati najprimernejši poseg za vaše želje in potrebe.",
    imageUrl: "https://images.unsplash.com/photo-1616394584738-fc6e612e71b9?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    category: "procedures",
    date: "2024-03-15",
    author: "Dr. Danijel Novak",
    readTime: "8 min"
  }
];

const BlogPage = () => {
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  // Filter posts by category and search query
  const filteredPosts = blogPosts.filter(post => {
    const matchesCategory = activeCategory === "all" || post.category === activeCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    
    return matchesCategory && matchesSearch;
  });

  // Format date function
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('sl-SI', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <Layout>
      <PageHeader
        title="Nasveti in novice"
        description="Strokovna mnenja, nasveti in novosti s področja estetske in rekonstruktivne kirurgije."
      />
      
      {/* Search and Filter Section */}
      <section className="bg-white py-12">
        <div className="container mx-auto px-6 md:px-8">
          {/* Search Bar */}
          <div className="max-w-xl mx-auto mb-8">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <Search className="w-5 h-5 text-gray-400" />
              </div>
              <Input
                type="search"
                placeholder="Iskanje člankov..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-medical-500"
              />
            </div>
          </div>
          
          {/* Categories Filter */}
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map(category => (
              <Button 
                key={category.id}
                variant={activeCategory === category.id ? "default" : "outline"}
                className={`rounded-full ${activeCategory === category.id ? 'bg-medical-500' : 'border-medical-300'}`}
                onClick={() => setActiveCategory(category.id)}
              >
                {category.name}
              </Button>
            ))}
          </div>
        </div>
      </section>
      
      {/* Blog Posts Grid */}
      <section className="bg-gray-50 py-12 md:py-16">
        <div className="container mx-auto px-6 md:px-8">
          {filteredPosts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-xl text-gray-700">Ni najdenih člankov za vaše iskalne pogoje.</p>
              <Button 
                variant="outline" 
                className="mt-4 border-medical-300" 
                onClick={() => {
                  setSearchQuery("");
                  setActiveCategory("all");
                }}
              >
                Počisti filtre
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map(post => (
                <Link 
                  key={post.id} 
                  to={`/nasveti/${post.slug}`}
                  className="block h-full"
                >
                  <article className="glass-card h-full overflow-hidden transition-all duration-300 hover:translate-y-[-5px]">
                    <div className="h-48 overflow-hidden">
                      <img 
                        src={post.imageUrl} 
                        alt={post.title}
                        className="w-full h-full object-cover transition-transform hover:scale-105"
                      />
                    </div>
                    
                    <div className="p-6">
                      <div className="flex justify-between items-center mb-3">
                        <span className="text-sm text-medical-600 font-medium">
                          {categories.find(cat => cat.id === post.category)?.name}
                        </span>
                        <span className="text-xs text-gray-500">
                          {formatDate(post.date)}
                        </span>
                      </div>
                      
                      <h3 className="text-xl font-bold text-gray-900 mb-3">
                        {post.title}
                      </h3>
                      
                      <p className="text-gray-700 mb-4 line-clamp-3">
                        {post.excerpt}
                      </p>
                      
                      <div className="flex justify-between items-center mt-4">
                        <span className="text-sm text-gray-600">
                          {post.author}
                        </span>
                        <span className="text-xs text-gray-500">
                          {post.readTime} branja
                        </span>
                      </div>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
      
      <CallToAction />
    </Layout>
  );
};

export default BlogPage;